import { useEffect, useState } from "react";
import { KeyRound, Phone, Menu, X } from "lucide-react";
import { PHONE_DISPLAY, PHONE_TEL, NAV_LINKS, scrollToId } from "@/site";
import { useQuote } from "@/quote-context";

export const Header = () => {
    const [scrolled, setScrolled] = useState(false);
    const [menuOpen, setMenuOpen] = useState(false);
    const { openQuote } = useQuote();

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 24);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    const go = (id) => {
        setMenuOpen(false);
        scrollToId(id);
    };

    return (
        <header
            data-testid="site-header"
            className={`fixed inset-x-0 top-0 z-40 transition-[background-color,box-shadow] duration-500 ${
                scrolled || menuOpen ? "bg-navy-950/95 shadow-lg shadow-black/20 backdrop-blur-md" : "bg-navy-950/0"
            }`}
        >
            <div className="mx-auto flex h-[72px] w-full max-w-7xl items-center justify-between gap-4 px-5 sm:px-8">
                <button data-testid="header-logo" onClick={() => go("home")} className="flex items-center gap-3 text-left">
                    <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-gold text-navy-950">
                        <KeyRound className="h-5 w-5" />
                    </span>
                    <span className="leading-tight">
                        <span className="block font-display text-base font-semibold text-white sm:text-lg">
                            Regina Expert Locksmith
                        </span>
                        <span className="block text-[10px] font-bold uppercase tracking-[0.22em] text-gold">
                            Family-Owned • Regina, SK
                        </span>
                    </span>
                </button>

                <nav className="hidden items-center gap-7 lg:flex" aria-label="Primary">
                    {NAV_LINKS.map((l) => (
                        <button
                            key={l.id}
                            data-testid={`nav-${l.id}`}
                            onClick={() => go(l.id)}
                            className="text-sm font-semibold text-slate-300 transition-colors duration-300 hover:text-white"
                        >
                            {l.label}
                        </button>
                    ))}
                </nav>

                <div className="flex items-center gap-3">
                    <a
                        data-testid="header-call-button"
                        href={PHONE_TEL}
                        className="hidden items-center gap-2 rounded-full bg-gold px-5 py-2.5 text-sm font-extrabold text-navy-950 transition-[transform,background-color] duration-300 hover:-translate-y-0.5 hover:bg-gold-light sm:inline-flex"
                    >
                        <Phone className="h-4 w-4" />
                        {PHONE_DISPLAY}
                    </a>
                    <a
                        data-testid="header-call-icon"
                        href={PHONE_TEL}
                        aria-label={`Call ${PHONE_DISPLAY}`}
                        className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-gold text-navy-950 sm:hidden"
                    >
                        <Phone className="h-4 w-4" />
                    </a>
                    <button
                        data-testid="header-quote-button"
                        onClick={() => openQuote()}
                        className="hidden rounded-full border border-white/25 px-5 py-2.5 text-sm font-bold text-white transition-colors duration-300 hover:bg-white hover:text-navy-950 md:block"
                    >
                        Free Quote
                    </button>
                    <button
                        data-testid="header-menu-button"
                        onClick={() => setMenuOpen(!menuOpen)}
                        aria-label="Toggle menu"
                        className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-white/25 text-white lg:hidden"
                    >
                        {menuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
                    </button>
                </div>
            </div>

            {menuOpen && (
                <div data-testid="mobile-menu" className="border-t border-white/10 bg-navy-950 px-5 py-4 lg:hidden">
                    {NAV_LINKS.map((l) => (
                        <button
                            key={l.id}
                            data-testid={`mobile-nav-${l.id}`}
                            onClick={() => go(l.id)}
                            className="block w-full py-3 text-left text-base font-semibold text-slate-200 transition-colors hover:text-white"
                        >
                            {l.label}
                        </button>
                    ))}
                    <button
                        data-testid="mobile-nav-quote"
                        onClick={() => {
                            setMenuOpen(false);
                            openQuote();
                        }}
                        className="mt-2 w-full rounded-full bg-gold py-3 text-sm font-extrabold uppercase tracking-wider text-navy-950"
                    >
                        Get a Free Quote
                    </button>
                </div>
            )}
        </header>
    );
};
